import {HocuspocusProvider, onCloseParameters, WebSocketStatus} from '@hocuspocus/provider';
import type {Provider} from '@lexical/yjs';
import {EmployeeLdap} from 'client';
import {isProd} from 'helpers/environment';
import {getCollaborationUrl} from 'utilities';
import * as Y from 'yjs';

const DOCUMENT_DISCONNECT_TIMEOUT = 30_000;
const COLLAB_ACTIVITY_STATE_EVENT = 'pulse:collab-activity-state';

type CollabActivityState = 'active' | 'typing' | 'idle' | 'commenting';

const isCollabActivityState = (state: unknown): state is CollabActivityState =>
  state === 'active' || state === 'typing' || state === 'idle' || state === 'commenting';

type CachedDoc = {
  doc: Y.Doc;
  refCount: number;
};

const docCache = new Map<string, CachedDoc>();

export const getCollabDocumentName = (collabId: string) =>
  `document-${isProd ? '' : 'test-'}${collabId}`;

export interface CollabUser {
  id: string;
  ldap: string;
  name: string;
  avatar?: string;
  cursor?: {x: number; y: number};
}

export function createWebsocketProvider(
  token: string,
  docId: string,
  yjsDocMap: Map<string, Y.Doc>,
  onConnect?: () => void,
  onDisconnect?: () => void,
  onUsersChange?: (users: CollabUser[]) => void,
  onSync?: () => void,
  employee?: EmployeeLdap
): Provider {
  let cachedDoc = docCache.get(docId);
  if (!cachedDoc || cachedDoc.doc.isDestroyed) {
    cachedDoc = {
      doc: new Y.Doc(),
      refCount: 0,
    };
    docCache.set(docId, cachedDoc);
  }

  cachedDoc.refCount += 1;
  const {doc} = cachedDoc;
  yjsDocMap.set(docId, doc);

  const provider = new HocuspocusProvider({
    url: getCollaborationUrl(),
    name: docId,
    token,
    document: doc,
    onConnect,
    onDisconnect,
    onClose: ({event}: onCloseParameters) => {
      if (event?.code !== 1005) {
        console.log('Connection closed. Code:', event?.code, 'Reason:', event?.reason);
      }
    },
  });

  let isDestroyed = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let disconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let activityDebounce: ReturnType<typeof setTimeout> | null = null;
  let releaseDocRef = false;
  let cleanupAwarenessChange: (() => void) | null = null;
  let cleanupSyncedListener: (() => void) | null = null;
  let cleanupUnsyncedChangesListener: (() => void) | null = null;
  let activityClearTimer: ReturnType<typeof setTimeout> | null = null;
  let activityOverride: CollabActivityState | null = null;
  // Hocuspocus also reports unsynced changes during initial/reconnect sync.
  // Only browser-warn after this tab has produced a local document update.
  let hasPendingLocalDocumentUpdate = false;

  const visibleActivityState = () => (document.hidden ? 'idle' : (activityOverride ?? 'active'));

  const setLocalActivityState = (state: CollabActivityState) => {
    if (!provider.awareness || !employee || isDestroyed) {
      return;
    }

    provider.awareness.setLocalStateField('activity', {
      state,
      updatedAt: Date.now(),
    });

    if (activityClearTimer) {
      clearTimeout(activityClearTimer);
      activityClearTimer = null;
    }

    if (state === 'typing') {
      activityClearTimer = setTimeout(() => {
        activityClearTimer = null;
        setLocalActivityState(visibleActivityState());
      }, 5_000);
    }
  };

  const handleActivityStateEvent = (event: Event) => {
    const detail = (event as CustomEvent<{collabId?: unknown; state?: unknown}>).detail;
    if (typeof detail?.collabId === 'string' && detail.collabId !== docId) {
      return;
    }

    const state = detail?.state;
    if (!isCollabActivityState(state)) {
      return;
    }

    activityOverride = state === 'commenting' ? 'commenting' : null;
    setLocalActivityState(state === 'active' ? visibleActivityState() : state);
  };

  const handleDocumentUpdate = (_update: Uint8Array, origin: unknown) => {
    if (isDestroyed) return;

    if (origin !== provider) {
      hasPendingLocalDocumentUpdate = true;
      setLocalActivityState(activityOverride ?? 'typing');
    }
  };

  const handleUnsyncedChanges = ({number}: {number: number}) => {
    if (number === 0) {
      hasPendingLocalDocumentUpdate = false;
    }
  };

  const clearLocalPendingIfProviderIsClean = () => {
    if (!provider.hasUnsyncedChanges) {
      hasPendingLocalDocumentUpdate = false;
    }
  };

  doc.on('update', handleDocumentUpdate);
  provider.on('unsyncedChanges', handleUnsyncedChanges);
  cleanupUnsyncedChangesListener = () => provider.off('unsyncedChanges', handleUnsyncedChanges);

  const safeDisconnect = () => {
    if (isDestroyed) return;

    try {
      provider.disconnect();
    } catch (e) {
      console.error('Error disconnecting collaboration provider:', e);
    }
  };

  const releaseDoc = () => {
    if (releaseDocRef) return;
    releaseDocRef = true;

    const currentCachedDoc = docCache.get(docId);
    if (currentCachedDoc?.doc !== doc) {
      if (yjsDocMap.get(docId) === doc) {
        yjsDocMap.delete(docId);
      }
      return;
    }

    currentCachedDoc.refCount -= 1;
    if (currentCachedDoc.refCount <= 0) {
      docCache.delete(docId);
      if (yjsDocMap.get(docId) === doc) {
        yjsDocMap.delete(docId);
      }
      queueMicrotask(() => doc.destroy());
    }
  };

  // Helper to reconnect with clean state
  const reconnect = () => {
    if (isDestroyed) return;

    if (provider.configuration.websocketProvider.status !== WebSocketStatus.Disconnected) {
      return;
    }

    safeDisconnect(); // Clear any stale state

    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
    }

    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      if (isDestroyed) return;

      clearLocalPendingIfProviderIsClean();

      provider.connect().catch((err: string) => {
        console.error('❌ Reconnection failed:', err);
      });
    }, 50);
  };

  // Tab visibility handling - disconnect after 30s when hidden, reconnect when visible
  const handleVisibilityChange = () => {
    if (isDestroyed) return;

    if (document.hidden) {
      setLocalActivityState('idle');

      if (disconnectTimer) {
        clearTimeout(disconnectTimer);
      }

      disconnectTimer = setTimeout(() => {
        disconnectTimer = null;
        safeDisconnect();
      }, DOCUMENT_DISCONNECT_TIMEOUT);
    } else {
      setLocalActivityState(visibleActivityState());

      // Cancel disconnect if they come back quickly
      if (disconnectTimer) {
        clearTimeout(disconnectTimer);
        disconnectTimer = null;
      }

      // Reconnect when tab becomes visible if disconnected
      reconnect();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Check if tab was opened in the background
  if (document.hidden) {
    disconnectTimer = setTimeout(() => {
      disconnectTimer = null;
      safeDisconnect();
    }, DOCUMENT_DISCONNECT_TIMEOUT);
  }

  // User activity detection - reconnect on interaction after idle kick
  const handleUserActivity = () => {
    if (isDestroyed) return;
    if (activityDebounce) return;

    activityDebounce = setTimeout(() => {
      setLocalActivityState(visibleActivityState());
      reconnect();
      activityDebounce = null;
    }, 500);
  };

  // Listen to pointerdown and keydown (covers mouse, touch, keyboard)
  document.addEventListener('pointerdown', handleUserActivity, {capture: true, passive: true});
  document.addEventListener('keydown', handleUserActivity, {capture: true, passive: true});
  window.addEventListener(COLLAB_ACTIVITY_STATE_EVENT, handleActivityStateEvent);

  // Clean disconnect on page unload to avoid phantom connections
  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (isDestroyed) return;

    const hasUnsaved = hasPendingLocalDocumentUpdate && provider.hasUnsyncedChanges;

    // Warn if there are unsaved changes
    if (hasUnsaved) {
      const message =
        'You have unsaved changes that have not synced. Are you sure you want to leave?';
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign, deprecation/deprecation
      event.returnValue = message;
      return message;
    }

    // Clean disconnect
    safeDisconnect();
  };

  window.addEventListener('beforeunload', handleBeforeUnload);

  // Clean up event listeners when provider is destroyed
  const originalDestroy = provider.destroy.bind(provider);
  provider.destroy = () => {
    if (isDestroyed) return;
    isDestroyed = true;

    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (disconnectTimer) {
      clearTimeout(disconnectTimer);
      disconnectTimer = null;
    }
    if (activityDebounce) {
      clearTimeout(activityDebounce);
      activityDebounce = null;
    }
    if (activityClearTimer) {
      clearTimeout(activityClearTimer);
      activityClearTimer = null;
    }

    window.removeEventListener('beforeunload', handleBeforeUnload);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    document.removeEventListener('pointerdown', handleUserActivity, {capture: true});
    document.removeEventListener('keydown', handleUserActivity, {capture: true});
    window.removeEventListener(COLLAB_ACTIVITY_STATE_EVENT, handleActivityStateEvent);

    cleanupAwarenessChange?.();
    cleanupAwarenessChange = null;
    cleanupSyncedListener?.();
    cleanupSyncedListener = null;
    cleanupUnsyncedChangesListener?.();
    cleanupUnsyncedChangesListener = null;
    doc.off('update', handleDocumentUpdate);

    try {
      originalDestroy();
    } finally {
      releaseDoc();
    }
  };

  // Set up awareness for user presence if callback is provided
  if (onUsersChange && provider.awareness && employee) {
    const awareness = provider.awareness;

    awareness.setLocalStateField('user', {
      id: employee.user_id,
      ldap: employee.ldap,
      name: employee.name,
    });
    setLocalActivityState(visibleActivityState());

    const handleAwarenessChange = () => {
      const states = Array.from(awareness.getStates().values());

      const users = states
        .map((state: any) => {
          let user = state.user || (state.name ? state : null);

          if (user && user.name && !user.id && user.name === employee.name) {
            user = {
              id: employee.user_id,
              ldap: employee.ldap,
              name: employee.name,
            };
          }

          return user;
        })
        .filter((u): u is CollabUser => u && u.id && u.name);

      onUsersChange(users);
    };

    awareness.on('change', handleAwarenessChange);
    cleanupAwarenessChange = () => awareness.off('change', handleAwarenessChange);

    handleAwarenessChange();
  }

  // Set up sync event listener if callback is provided
  if (onSync) {
    const handleSynced = () => {
      onSync();
    };

    provider.on('synced', handleSynced);
    cleanupSyncedListener = () => provider.off('synced', handleSynced);

    if (provider.synced) {
      onSync();
    }
  }

  return provider as unknown as Provider;
}

export function getYDocFromCache(docId: string): Y.Doc | undefined {
  return docCache.get(docId)?.doc;
}

export function clearYDocFromCache(docId: string): void {
  const cachedDoc = docCache.get(docId);
  if (cachedDoc && cachedDoc.refCount <= 0) {
    cachedDoc.doc.destroy();
    docCache.delete(docId);
  }
}
